import React, { useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useCallback } from 'react';
import { useState } from 'react';
import { Tag } from 'primereact/tag';

const get = (row, key) => row?.[key] ?? row?.[key?.charAt(0).toUpperCase() + key?.slice(1)] ?? null;

const formatDateOnly = (value) => {
    if (!value) return '';
    const d = typeof value === 'string' || typeof value === 'number' ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString();
};

const EventTypeChip = ({ type }) => {
    const { th } = useLanguage();
    const t = (type || '').toString().toLowerCase();

    const isPurchase = t === 'purchase';
    const isClaim = t === 'claim';

    const label = isPurchase ? th('dealersStockView.history.eventTypes.purchase') : isClaim ? th('dealersStockView.history.eventTypes.claim') : type || '-';

    const severity = isPurchase ? 'info' : isClaim ? 'warning' : null;

    return <Tag value={label} severity={severity} rounded className="px-2 py-1 border-none" />;
};

const buildRowKey = (row, idx = 0) => {
    const et = (row.eventType ?? row.EventType ?? '').toString();
    const pc = (row.partCode ?? row.PartCode ?? '').toString();
    const cn = (row.claimNo ?? row.ClaimNo ?? '-').toString();
    const ed = row.eventDate ?? row.EventDate;
    const ts = ed ? new Date(ed).getTime() : 0;
    return `${et}|${pc}|${cn}|${ts}|${idx}`;
};

const PartHistoryDialog = ({ visible, onHide, rows, totalCount, pagination, onPageChange, loading, headerInfo }) => {
    const { th } = useLanguage();

    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);

    const headerTitle = useMemo(() => {
        const pc = headerInfo?.PartDesc ? ` • ${headerInfo.PartDesc}` : '';
        return `${th('dealersStockView.history.title')}${pc}`;
    }, [headerInfo, th]);

    const data = Array.isArray(rows) ? rows : [];

    const keyedData = useMemo(() => data.map((r, i) => ({ ...r, _key: r._key ?? buildRowKey(r, i) })), [data]);

    const sortedData = useMemo(() => {
        if (!sortField || sortOrder === 0) return keyedData;

        return [...keyedData].sort((a, b) => {
            let av = get(a, sortField);
            let bv = get(b, sortField);

            const isDateField = sortField.toLowerCase().includes('date') || ['eventDate', 'receiptDate'].includes(sortField);
            if (isDateField) {
                av = av ? new Date(av).getTime() : null;
                bv = bv ? new Date(bv).getTime() : null;
            } else {
                const avNum = Number(av);
                const bvNum = Number(bv);
                if (!Number.isNaN(avNum) && !Number.isNaN(bvNum)) {
                    av = avNum;
                    bv = bvNum;
                }
            }

            if (typeof av === 'string') av = av.toLowerCase();
            if (typeof bv === 'string') bv = bv.toLowerCase();

            if (av == null && bv == null) return 0;
            if (av == null) return sortOrder;
            if (bv == null) return -sortOrder;

            if (typeof av === 'number' && typeof bv === 'number') {
                return sortOrder * (av - bv);
            }

            if (av < bv) return -sortOrder;
            if (av > bv) return sortOrder;
            return 0;
        });
    }, [keyedData, sortField, sortOrder]);

    const handleSort = useCallback((e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
    }, []);

    return (
        <Dialog header={headerTitle} visible={visible} style={{ width: '80vw', maxWidth: 1100 }} modal onHide={onHide} dismissableMask>
            <DataTable
                value={sortedData}
                dataKey="_key"
                lazy
                paginator
                first={(pagination?.PageNumber - 1) * (pagination?.PageSize || 10)}
                rows={pagination?.PageSize || 10}
                totalRecords={totalCount}
                onPage={onPageChange}
                loading={loading}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                showGridlines
                stripedRows
                emptyMessage={th('dealersStockView.history.noData')}
                tableStyle={{ minWidth: '60rem' }}
                sortMode="single"
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={handleSort}
            >
                <Column field="eventType" header={th('dealersStockView.history.event')} body={(row) => <EventTypeChip type={get(row, 'eventType')} />} style={{ width: '12rem' }} sortable />
                <Column field="eventDate" header={th('dealersStockView.history.eventDate')} body={(row) => formatDateOnly(get(row, 'eventDate'))} style={{ width: '16%' }} sortable />
                <Column field="receiptDate" header={th('dealersStockView.history.receiptDate')} body={(row) => formatDateOnly(get(row, 'receiptDate'))} style={{ width: '16%' }} sortable />
                <Column field="claimNo" header={th('dealersStockView.history.claimNo')} style={{ width: '14%' }} sortable />
                <Column field="chassisNo" header={th('fullServiceInvoice.columns.chassisNo')} style={{ width: '14%' }} sortable />
                <Column
                    field="quantity"
                    header={th('dealersStockView.stock.quantity')}
                    body={(row) => {
                        const q = get(row, 'quantity');
                        const u = get(row, 'uom');
                        return u ? `${q} ${u}` : q;
                    }}
                    style={{ width: '12%' }}
                    sortable
                    dataType="numeric"
                />
            </DataTable>
        </Dialog>
    );
};

export default PartHistoryDialog;
